import React, { useRef } from "react";
import {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Button,
  IconButton,
} from "@chakra-ui/react";
import { X } from "lucide-react";

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  cancelLabel: string;
  confirmLabel: string;
  closeAriaLabel?: string;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  cancelLabel,
  confirmLabel,
  closeAriaLabel = "Close",
}) => {
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
      isCentered
    >
      <AlertDialogOverlay>
        <AlertDialogContent
          maxW="572px"
          padding="40px 32px"
          borderRadius="8px"
          position="relative"
        >
          <IconButton
            aria-label={closeAriaLabel}
            icon={<X size={16} />}
            variant="ghost"
            size="sm"
            position="absolute"
            top="16px"
            right="16px"
            borderRadius="30px"
            bg="rgba(0, 0, 0, 0.5)"
            color="white"
            _hover={{ bg: "rgba(0, 0, 0, 0.7)" }}
            onClick={onClose}
          />

          <AlertDialogHeader
            fontSize="24px"
            fontWeight="600"
            color="#313F4D"
            lineHeight="1em"
            p={0}
            mb="24px"
          >
            {title}
          </AlertDialogHeader>

          <AlertDialogBody
            fontSize="16px"
            fontWeight="400"
            color="#3E4F61"
            lineHeight="1.21em"
            p={0}
            mb="24px"
          >
            {description}
          </AlertDialogBody>

          <AlertDialogFooter p={0} gap="24px">
            <Button
              ref={cancelRef}
              variant="outline"
              borderColor="#086AA9"
              color="#086AA9"
              borderRadius="8px"
              fontWeight="400"
              fontSize="16px"
              h="40px"
              flex={1}
              onClick={onClose}
            >
              {cancelLabel}
            </Button>
            <Button
              bg="#086AA9"
              color="white"
              borderRadius="8px"
              fontWeight="400"
              fontSize="16px"
              h="40px"
              flex={1}
              _hover={{ bg: "#075a91" }}
              onClick={onConfirm}
            >
              {confirmLabel}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};
